<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { formatWithoutRounding } from "../../mixins/productPrice.mixin";
import ShoppingCartSummary from "./shoppingCartSummary.vue";
import { formatCurrency } from "../../utils/currencyHelpers";
import ShoppingCartKsps from "./shoppingCartKsps.vue";
import ShoppingCartDiscounts from "./shoppingCartDiscounts.vue";
import autoAnimate from "@formkit/auto-animate";
import LineItem from "./lineItem.vue";
import { API_ERROR_MESSAGES } from "../../utils/API/APIErrors";
import LineItemSkeleton from "./lineItemSkeleton.vue";
import TopTextSkeleton from "./topTextSkeleton.vue";
import PaymentProviders from "./paymentProviders.vue";
import { CheckoutStages } from "../../services/models/checkout.models";
import { useCheckoutStateStore } from "../checkout/stores/checkoutStateStore";
import CheckoutService from "../../services/checkout.service";
import { useShoppingCartStore } from "./stores/shoppingCartStore";
import ShoppingCartService from "../../services/shoppingcart.service";
import ProductsModuleContent from "../../views/productsModule/productsModuleContent.vue";
import { useCartPageUpsell } from "../../composables/useUpsell";
import { HotjarTrackingService } from "../../services/hotjarTracking.service";

const props = defineProps<{
  ksps: string | null; // JSON Content from NewCheckoutIndex.cshtml
  maxCartSize?: string;
  initialLineItemCount?: string; // Number of line items in the cart when the component is mounted
  isQuickAdd?: boolean;
}>();

const MOBILE_UPSELL_DISPLAY_TRESHOLD = 1023;
const shoppingCartStore = useShoppingCartStore();
const checkoutStateStore = useCheckoutStateStore();
const INITIAL_LINE_ITEM_COUNT = ref(Number(props.initialLineItemCount) || 3);
const MAX_CART_SIZE = ref(Number(props.maxCartSize) || 99);
const inStockListElement = ref();
const outOfStockListElement = ref();

watch(
  () => shoppingCartStore.cart?.itemCount,
  (newVal) => {
    localStorage.setItem("totalProductCount", JSON.stringify(newVal));
  },
  { deep: true },
);

const mobileUpsellVisible = ref(
  window.innerWidth < MOBILE_UPSELL_DISPLAY_TRESHOLD,
);
const handleResize = () => {
  mobileUpsellVisible.value =
    window.innerWidth < MOBILE_UPSELL_DISPLAY_TRESHOLD;
};

/**
 * Computes the current size of the shopping cart. (Adds together all quantity values of lineItems)
 *
 * @returns {number} The current size of the shopping cart.
 */
const currentCartSize = computed(() => {
  return shoppingCartStore.currentCartSize;
});

/**
 * Determines if the shopping cart is full.
 * @returns {boolean} True if the shopping cart is full, false otherwise.
 */
const cartIsFull = computed(() => {
  if (!currentCartSize.value) return false;
  return currentCartSize.value >= MAX_CART_SIZE.value;
});

/**
 * Computed property for the top text in the shopping cart view on mobile viewports.
 * Conditionally contains shipping cost if the cart qualifies for free shipping.
 *
 * @returns {string} The top text value.
 */
const topText = computed(() => {
  if (!shoppingCartStore.cart) return;

  const baseText = `${currentCartSize.value} varer / Totalsum: ${shoppingCartStore.cart.total && formatCurrency(shoppingCartStore.cart.total.currency)} ${shoppingCartStore.cart.total && formatWithoutRounding(shoppingCartStore.totalAmount)}`;
  return shoppingCartStore.freeShipping ? `${baseText} (Fri frakt)` : baseText;
});

/**
 * If the new cart value contains item(s) with a quantity that exceeds the current inventory, add it to the LineItemsOutOfStock array if it's not already there.
 * Also remove the item from the cart so that the summary is updated.
 *
 * @param {Object} cart - The new value of the 'cart' property.
 */
watch(
  () => shoppingCartStore.cart,
  (newValue) => {
    newValue?.lineItems.forEach((lineItem) => {
      if (lineItem.quantity <= lineItem.currentInventory) return;

      if (
        !shoppingCartStore.lineItemsOutOfStock?.some(
          (x) => x.ean === lineItem.ean,
        )
      ) {
        shoppingCartStore.lineItemsOutOfStock.push(lineItem);
      }

      ShoppingCartService.apiRemoveProductFromCart(lineItem.code);
    });
  },
);

/**
 * If cartIsFull becomes false, remove the summaryError value.
 *
 * @param {boolean} newValue - The new value of the 'cartIsFull' property.
 */
watch(cartIsFull, (newValue) => {
  if (!newValue) {
    shoppingCartStore.setSummaryError("");
  }
});

/**
 * Clears the discountError value.
 */
const handleResetDiscountError = () => {
  shoppingCartStore.setDiscountError("");
};

const handleGoToCheckout = () => {
  CheckoutService.goToCheckoutStage(CheckoutStages.Verification);
};

onMounted(() => {
  window.addEventListener("resize", handleResize);

  if (props.ksps) {
    shoppingCartStore.setKsps(JSON.parse(props.ksps));
  }

  autoAnimate(inStockListElement.value);
  autoAnimate(outOfStockListElement.value);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});

const variantKeys = computed(() => {
  const cart = shoppingCartStore.cart;
  return cart && cart.lineItems
    ? cart.lineItems.map((lineItem) => lineItem.esalesKey)
    : [];
});

const { upsellModel, isLoading } = useCartPageUpsell({
  variantKeys,
  trigger: variantKeys,
});
</script>

<template>
  <div>
    <div class="layout-new-checkout__top">
      <h1>{{ $t("shoppingCart") }}</h1>
      <div class="layout-new-checkout__top-content">
        <span v-if="topText" class="layout-new-checkout__top-text">{{
          topText
        }}</span>
        <TopTextSkeleton
          v-if="checkoutStateStore.isLoading"
          class="layout-new-checkout__top-text"
        />
      </div>
    </div>
    <div class="layout-new-checkout">
      <div class="layout-new-checkout__heading">
        <h1>{{ $t("shoppingCart") }}</h1>
      </div>
      <div class="layout-new-checkout__main">
        <div class="layout-new-checkout__vertical-split">
          <div
            class="layout-new-checkout__primary layout-new-checkout__element"
          >
            <div
              class="layout-new-checkout__primary-content layout-new-checkout__primary-content--shoppingcart"
            >
              <h2 class="sr-only">{{ $t("products") }}</h2>
              <ul
                ref="outOfStockListElement"
                :aria-hidden="!shoppingCartStore.lineItemsOutOfStock?.length"
                class="checkout-line-items checkout-line-items--out-of-stock"
              >
                <LineItem
                  v-for="lineItem in shoppingCartStore.lineItemsOutOfStock"
                  :key="lineItem.ean"
                  :line-item="lineItem"
                  :out-of-stock="true"
                  :error="API_ERROR_MESSAGES.OUT_OF_STOCK"
                ></LineItem>
              </ul>
              <ul
                ref="inStockListElement"
                :aria-hidden="!shoppingCartStore.lineItemsOutOfStock?.length"
                class="checkout-line-items"
              >
                <LineItem
                  v-for="lineItem in shoppingCartStore.lineItemsInStock"
                  :key="lineItem.ean"
                  :line-item="lineItem"
                  :error="shoppingCartStore.lineItemErrors?.[lineItem.ean]"
                ></LineItem>
              </ul>
              <div
                v-if="checkoutStateStore.isLoadingCart"
                class="checkout-line-items"
              >
                <LineItemSkeleton
                  v-for="(_, index) in INITIAL_LINE_ITEM_COUNT"
                  :key="index"
                ></LineItemSkeleton>
              </div>
              <template
                v-if="
                  !shoppingCartStore.lineItemsInStock?.length &&
                  !shoppingCartStore.lineItemsOutOfStock?.length &&
                  !checkoutStateStore.isLoading &&
                  !checkoutStateStore.isLoadingCart
                "
              >
                <h3 class="layout-new-checkout__primary-placeholder">
                  {{ $t("shoppingCartEmpty") }}
                </h3>
              </template>
            </div>
            <template v-for="(upsellItem, index) in upsellModel" :key="index">
              <ProductsModuleContent
                v-if="upsellItem.primaryList"
                :key="index"
                v-show="!mobileUpsellVisible"
                :heading="
                  upsellItem.primaryList.label ?? $t('maybeYouAlsoLike')
                "
                :search-result-recommend-list="upsellItem"
                :user-authenticated="
                  shoppingCartStore.cart?.isAuthenticated ?? false
                "
                :favorite-form-hidden="true"
                :is-loading="isLoading"
                :hide-sizes="true"
                :hide-colors="true"
                :hide-favorites="true"
                :is-quick-add="props.isQuickAdd"
                :productCount="upsellItem.primaryList.productGroups.length"
                :use-skeleton-loaders="true"
                :track-view-item-list="!mobileUpsellVisible"
                :track-select-item="true"
                :item-list-id="`shoppingcart-${upsellItem.primaryList.algorithm}`"
                :item-list-name="`shoppingcart-${upsellItem.primaryList.algorithm}`"
                :upsell-algorithm="upsellItem.primaryList.algorithm"
                container-class="product-list__upsell-small product-list__upsell-small--desktop"
                :use-slider-for-desktop="true"
                :teaser-product-count="4"
              />
            </template>
          </div>
          <div class="layout-new-checkout__secondary">
            <div
              v-if="shoppingCartStore.lineItemsInStock?.length"
              class="layout-new-checkout layout-new-checkout__element layout-new-checkout__element-small"
            >
              <h2 class="accordion-new__heading">Legg til rabattkode</h2>
              <ShoppingCartDiscounts
                :discounts="shoppingCartStore.cart?.appliedCoupons || {}"
                @resetDiscountError="handleResetDiscountError"
                @apiAddDiscountCode="ShoppingCartService.apiAddDiscountCode"
                @apiRemoveDiscountCode="
                  ShoppingCartService.apiRemoveDiscountCode
                "
              />
            </div>
            <div
              v-if="shoppingCartStore.cart"
              class="layout-new-checkout__summary layout-new-checkout__element"
            >
              <ShoppingCartSummary
                :cart="shoppingCartStore.cart"
                :error="shoppingCartStore.summaryError || ''"
                :show-checkout-link="!!shoppingCartStore.cart?.itemCount"
              />
            </div>
            <template v-for="(upsellItem, index) in upsellModel" :key="index">
              <ProductsModuleContent
                v-if="upsellItem.primaryList"
                :key="index"
                v-show="mobileUpsellVisible"
                :heading="
                  upsellItem.primaryList.label ?? $t('maybeYouAlsoLike')
                "
                :search-result-recommend-list="upsellItem"
                :show-as-column-on-mobile="false"
                :user-authenticated="
                  shoppingCartStore.cart?.isAuthenticated ?? false
                "
                :favorite-form-hidden="true"
                :is-loading="isLoading"
                :hide-sizes="true"
                :hide-colors="true"
                :hide-favorites="true"
                :is-quick-add="props.isQuickAdd"
                :productCount="upsellItem.primaryList.productGroups.length"
                :use-skeleton-loaders="true"
                :track-view-item-list="mobileUpsellVisible"
                :track-select-item="true"
                :item-list-id="`shoppingcart-${upsellItem.primaryList.algorithm}`"
                :item-list-name="`shoppingcart-${upsellItem.primaryList.algorithm}`"
                :upsell-algorithm="upsellItem.primaryList.algorithm"
                container-class="product-list__upsell-small product-list__upsell-small--mobile"
              />
            </template>
            <ShoppingCartKsps
              v-if="shoppingCartStore.ksps"
              :key-selling-points="shoppingCartStore.ksps"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="shoppingCartStore.cart?.subTotal?.amount"
      class="layout-new-checkout__summary-fixed"
    >
      <h4 class="layout-new-checkout__summary-fixed-heading">
        <span>{{ $t("total") }}: </span>
        <span
          >{{
            shoppingCartStore.cart.total &&
            formatCurrency(shoppingCartStore.cart.total.currency)
          }}
          {{
            shoppingCartStore.cart.total &&
            formatWithoutRounding(shoppingCartStore.totalAmount)
          }}</span
        >
      </h4>
      <button
        v-if="shoppingCartStore.cart?.itemCount"
        class="btn btn--profile btn--checkout checkout-form__submit"
        @click="handleGoToCheckout"
      >
        {{ $t("toCheckout") }}
      </button>

      <div class="layout-new-checkout__summary-fixed-payment-methods">
        <img
          v-for="(logo) in shoppingCartStore.paymentLogos"
          :key="logo.id"
          :src="logo.url"
          :alt="logo.alt"
        />
      </div>
    </div>
  </div>
</template>
